import { STORE_KPI } from '@dynameat/views/reporting/mutationTypes';
import { sentryLogException } from '@core/helpers/sentryLogger';
import * as api from './api';
import { MenuFilters, PriceChangeRestriction } from './models';
import * as KPITypes from './models/reporting/constants';
import * as MutationTypes from './mutationTypes';
import {
  EvolutiveCalculator,
  MarginCalculator,
  PopularityCalculator,
  ProcessingSpeedCalculator
} from './models/reporting/calculators';

export const menuFilterListRequest = async ({
  commit,
  getters: { menuFilterList }
}, pointOfSaleGuid) => {
  if (menuFilterList.length === 0) {
    const response = await api.menuFilterListRequest(pointOfSaleGuid);
    const menuFilters = MenuFilters.toInstanceList(response.data.results);
    commit(MutationTypes.MENU_FILTER_LIST_SUCCESS, menuFilters);
  }
};

export const restrictionIntervalListRequest = async ({
  commit,
  getters: { restrictionIntervalList }
}, pointOfSaleGuid) => {
  if (!restrictionIntervalList) {
    const response = await api.restrictionIntervalListRequest(pointOfSaleGuid);
    const restrictionIntervals = PriceChangeRestriction.toInstance(response.data.results[0]);
    commit(MutationTypes.RESTRICTION_INTERVAL_LIST_SUCCESS, restrictionIntervals);
  }
};

export const calculateProcessingSpeedKPI = async ({ commit, getters: { kpiStorage } }, productGuid) => {
  if (kpiStorage(KPITypes.PROCESSING_SPEED)(productGuid)?.default) {
    const calculator = new ProcessingSpeedCalculator(productGuid);
    try {
      const model = await calculator.calculateKPI();
      commit(STORE_KPI, model);
    } catch (err) {
      sentryLogException(err, 'There was an error loading processing speed KPI');
    }
  }
  return kpiStorage(KPITypes.PROCESSING_SPEED)(productGuid);
};

export const calculateMarginKPI = async ({ commit, getters: { kpiStorage } }, productGuid) => {
  if (kpiStorage(KPITypes.MARGIN)(productGuid)?.default) {
    const calculator = new MarginCalculator(productGuid);
    try {
      const model = await calculator.calculateKPI();
      commit(STORE_KPI, model);
    } catch (err) {
      sentryLogException(err, 'There was an error loading margin KPI');
    }
  }
  return kpiStorage(KPITypes.MARGIN)(productGuid);
};

export const calculatePopularityKPI = async ({ commit, getters: { kpiStorage } }, productGuid) => {
  if (kpiStorage(KPITypes.POPULARITY)(productGuid)?.default) {
    const calculator = new PopularityCalculator(productGuid);
    try {
      const model = await calculator.calculateKPI();
      commit(STORE_KPI, model);
    } catch (err) {
      sentryLogException(err, 'There was an error loading popularity KPI');
    }
  }
  return kpiStorage(KPITypes.POPULARITY)(productGuid);
};

export const getProductRadarKpis = async (context, productGuid) => {
  const speedKpi = await calculateProcessingSpeedKPI(context, productGuid);
  const marginKpi = await calculateMarginKPI(context, productGuid);
  const popularityKpi = await calculatePopularityKPI(context, productGuid);
  const responseKpis = {
    speed: speedKpi.kpi,
    stock: null,
    margin: marginKpi.kpi,
    cross_selling: null,
    popularity: popularityKpi.kpi,
    strategic: null,
  };
  return {
    speed: responseKpis.speed,
    stock: responseKpis.stock,
    margin: responseKpis.margin,
    crossSelling: responseKpis.cross_selling,
    popularity: responseKpis.popularity,
    strategic: responseKpis.strategic
  };
};

export const calculateEvolutiveKPI = async ({ commit, getters: { kpiStorage } }, {
  productGuid, startDate, endDate, serviceGuid
}) => {
  const serviceKey = (serviceGuid) ? `-${serviceGuid}` : '';
  const startDateToString = startDate.toDateString();
  const endDateToString = endDate.toDateString();
  const kpiKey = `${productGuid}-${startDateToString}-${endDateToString}${serviceKey}`;
  if (kpiStorage(KPITypes.EVOLUTIVE)(kpiKey)?.default) {
    const calculator = new EvolutiveCalculator(productGuid, startDate, endDate, serviceGuid);
    try {
      const model = await calculator.calculateKPI();
      commit(STORE_KPI, model);
    } catch (err) {
      sentryLogException(err, 'There was an error loading evolutive KPI');
    }
  }
  const result = kpiStorage(KPITypes.EVOLUTIVE)(kpiKey).kpi;
  if (!(result instanceof Object)) {
    return {};
  }
  return result;
};

export const setDateRangeFilter = ({ commit }, dateRange) => {
  commit(MutationTypes.SET_DATE_RANGE_FILTER, dateRange);
};

export const setMenuGuidFilter = ({ commit }, menuGuid) => {
  commit(MutationTypes.SET_MENU_GUID_FILTER, menuGuid);
};

export const setSectionGuidFilter = ({ commit }, sectionGuid) => {
  commit(MutationTypes.SET_SECTION_GUID_FILTER, sectionGuid);
};

export const setSaleRateFilter = ({ commit }, saleRate) => {
  commit(MutationTypes.SET_SALE_RATE_FILTER, saleRate);
};

export const undoPush = async ({ commit }, payload) => {
  commit(MutationTypes.UNDO_PUSH, payload);
};

export const undoBufferLengthModify = async ({ commit }, newLength) => {
  commit(MutationTypes.UNDO_BUFFER_LENGTH_MODIFY, newLength);
};

export const undoPop = async ({ commit, getters: { undo } }) => {
  commit(MutationTypes.UNDO_POP);
  return undo;
};

export const undoClear = async ({ commit }) => {
  commit(MutationTypes.UNDO_CLEAR);
};
